import { css } from '@emotion/react'
import { ActionCards } from 'components/actions/ActionCards'
import { Action } from 'components/actions/utils'
import { Block } from 'components/Block'
import { Button } from 'components/button/Button'
import { ContactAddModal } from 'components/contact/contactAddModal/ContactAddModal'
import { useDashboardSideModal } from 'components/content/dashboardSideModal/utils'
import { AccountsTab } from 'components/cornerstone/digitalVault/accountsTab/AccountsTab'
import { DevicesTab } from 'components/cornerstone/digitalVault/devicesTab/DevicesTab'
import { DocumentsTab } from 'components/cornerstone/digitalVault/documentsTab/DocumentsTab'
import { SharedTab } from 'components/cornerstone/digitalVault/sharedTab/SharedTab'
import { HeaderDashboard } from 'components/header/headerDashboard/HeaderDashboard'
import { Icon } from 'components/icon'
import { useModal } from 'components/modal/Modal'
import { PageTitle } from 'components/PageTitle'
import { ProfileWrapper } from 'components/profile/profileContent/ProfileContent'
import { ProfileSidebar } from 'components/profile/profileSidebar/ProfileSidebar'
import { SideLayout } from 'components/sideLayout/SideLayout'
import { HStack, Stack } from 'components/Stack'
import { RouterTabs } from 'components/tabs/RouterTabs'

import { TabSpecification } from 'components/tabs/Tabs'
import { Text } from 'components/Typography'
import { WizardContent } from 'components/wizard/wizardContent/WizardContent'
import { WizardHero } from 'components/wizard/wizardHero/WizardHero'
import { useTranslation } from 'next-i18next'
import { colors } from 'utils/colors'
import { isNonNullable } from 'utils/general'
import { pxToRem } from 'utils/styled'

export function DigitalVaultPage() {
  const { t } = useTranslation('digital_vault')

  const [addContactModal, showAddContactModal] = useModal(
    ({ isOpen, close }) => (isOpen ? <ContactAddModal onSuccess={() => close()} /> : null),
    {
      modalCss: css`
        max-width: ${pxToRem(580)}rem;
      `,
    }
  )

  const [sideModal, openSideModal] = useDashboardSideModal('DIGITAL_VAULT')

  const ActionOnlineAccounts: Action = {
    title: t('digital_vault.inside_page.actions.online_accounts.title'),
    description: t('digital_vault.inside_page.actions.online_accounts.desc'),
    buttonText: t('digital_vault.inside_page.actions.online_accounts.button'),
    icon: <Icon name="UserProfileTap" color={colors.gray[500]} />,
    url: '/me/site/add',
  }

  const ActionDigitalVaultDevices: Action = {
    title: t('digital_vault.inside_page.actions.digital_vault.title'),
    description: t('digital_vault.inside_page.actions.digital_vault.desc'),
    buttonText: t('digital_vault.inside_page.actions.digital_vault.button'),
    icon: <Icon name="PhoneLock" color={colors.gray[500]} />,
    url: '/me/device/add',
  }

  const ActionDocuments: Action = {
    title: t('digital_vault.inside_page.actions.documents.title'),
    description: t('digital_vault.inside_page.actions.documents.desc'),
    buttonText: t('digital_vault.inside_page.actions.documents.button'),
    icon: <Icon name="TextDocument" color={colors.gray[500]} />,
    url: '/me/document/add',
  }

  const ActionTrustedContacts: Action = {
    title: t('digital_vault.inside_page.actions.trusted_contacts.title'),
    description: t('digital_vault.inside_page.actions.trusted_contacts.desc'),
    buttonText: t('digital_vault.inside_page.actions.trusted_contacts.button'),
    icon: <Icon name="TrustedContact" color={colors.gray[500]} />,
  }

  const tabs: TabSpecification[] = [
    {
      key: 'accounts',
      label: t('digital_vault.inside_page.tabs.accounts'),
      content: <AccountsTab />,
    },
    {
      key: 'documents',
      label: t('digital_vault.inside_page.tabs.documents'),
      content: <DocumentsTab />,
    },
    {
      key: 'devices',
      label: t('digital_vault.inside_page.tabs.devices'),
      content: <DevicesTab />,
    },
    {
      key: 'shared-with-you',
      label: t('digital_vault.inside_page.tabs.shared_with_you'),
      content: <SharedTab />,
    },
  ]

  const actions: Action[] = [
    ActionOnlineAccounts,
    ActionDigitalVaultDevices,
    ActionDocuments,
    { ...ActionTrustedContacts, onClick: () => showAddContactModal() },
  ].filter(isNonNullable)

  return (
    <SideLayout left={<ProfileSidebar />} header={<HeaderDashboard />}>
      {addContactModal}
      {sideModal}

      <PageTitle title={t('digital_vault.inside_page.meta_title')} />

      <Block>
        <ProfileWrapper>
          <WizardContent
            hideBack
            css={{ marginBottom: '4rem' }}
            mainCss={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}
          >
            <WizardHero
              caption={t('digital_vault.inside_page.caption')}
              title={t('digital_vault.inside_page.title')}
              pictureKey={'illustrations/safe'}
            >
              <Stack space={'1rem'}>
                <Text variant={'body'}>{t('digital_vault.inside_page.desc')}</Text>
                <HStack space={'1rem'}>
                  <Button
                    type="button"
                    css={{ width: 'fit-content' }}
                    onClick={() => showAddContactModal()}
                  >
                    {t('digital_vault.inside_page.action_button')}
                  </Button>

                  <Button
                    type="button"
                    variant="secondary"
                    leftIcon={<Icon name="LightBulb" />}
                    css={{ width: 'fit-content' }}
                    onClick={() => openSideModal()}
                  >
                    {t('digital_vault.inside_page.info_button')}
                  </Button>
                </HStack>
              </Stack>
            </WizardHero>
            <ActionCards
              actions={actions}
              variant="text-button"
              buttonCss={css`
                align-self: flex-start;
              `}
            />
            <RouterTabs tabs={tabs} tabContentCss={{ paddingTop: '1.5rem' }} />
          </WizardContent>
        </ProfileWrapper>
      </Block>
    </SideLayout>
  )
}
